const BRACKET_CITATION_PATTERN = /\[(\d+)\](?!\()/g
const HTML_CITATION_PATTERN =
  /<(?:cite|source-citation)\s+([^>]+?)\s*(?:\/>|><\/source-citation>)/g
const FENCED_CODE_BLOCK_PATTERN = /```[\s\S]*?```/g
const INLINE_CODE_PATTERN = /`[^`\n]*`/g

export const maskMarkdownCodeSegments = (markdown: string) => {
  const replacements: string[] = []

  const mask = (value: string) => {
    const index = replacements.push(value) - 1
    return `__CHAT_CITATION_CODE_${index}__`
  }

  const maskedMarkdown = markdown
    .replace(FENCED_CODE_BLOCK_PATTERN, (match) => mask(match))
    .replace(INLINE_CODE_PATTERN, (match) => mask(match))

  const restore = (value: string) => {
    return value.replace(
      /__CHAT_CITATION_CODE_(\d+)__/g,
      (_match, indexText: string) => {
        const index = Number.parseInt(indexText, 10)
        return replacements[index] ?? ''
      },
    )
  }

  return {
    maskedMarkdown,
    restore,
  }
}

export const expandCitationIds = (idStr: string): string[] => {
  const ids: string[] = []
  const parts = idStr.split(',')

  for (const part of parts) {
    const range = part.trim().match(/^(\d+)-(\d+)$/)
    if (range) {
      const start = parseInt(range[1], 10)
      const end = parseInt(range[2], 10)
      // Prevent excessive expansion
      if (end - start < 100 && end >= start) {
        for (let i = start; i <= end; i++) {
          ids.push(i.toString())
        }
      }
    } else {
      const cleanId = part.trim()
      if (cleanId) ids.push(cleanId)
    }
  }
  return ids
}

const extractHtmlCitationAttributeIds = (attributes: string): string[] => {
  const idValues = [
    attributes.match(/\bid=["']([^"']+)["']/)?.[1],
    attributes.match(/data-id=["']([^"']+)["']/)?.[1],
    attributes.match(/data-ids=["']([^"']+)["']/)?.[1],
  ].filter((value): value is string => Boolean(value))

  return idValues.flatMap((value) => expandCitationIds(value))
}

export const extractCitationIds = (text: string): string[] => {
  const ids = new Set<string>()
  const { maskedMarkdown } = maskMarkdownCodeSegments(text)

  for (const match of maskedMarkdown.matchAll(BRACKET_CITATION_PATTERN)) {
    ids.add(match[1])
  }

  for (const match of maskedMarkdown.matchAll(HTML_CITATION_PATTERN)) {
    const attributes = match[1] ?? ''
    for (const id of extractHtmlCitationAttributeIds(attributes)) {
      ids.add(id)
    }
  }

  return Array.from(ids)
}

export const extractNumericCitationIds = (text: string): number[] => {
  return extractCitationIds(text)
    .map((id) => Number.parseInt(id, 10))
    .filter((id) => Number.isFinite(id))
}

export const hasCitations = (text: string): boolean => {
  return extractCitationIds(text).length > 0
}
